import { useEffect, useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import {
  UserPlus,
  Users,
  Search,
  Loader,
  X as XIcon,
  AlertCircle,
  Trash2,
  Info,
} from 'lucide-react';

import { incidentsApiService } from '../../services/incidentsApi';
import { useAuth } from '../../contexts/AuthContext';
import { useDebounce } from '../../hooks/useDebounce';
import { useStaffListSearch } from '../../hooks/useStaffListSearch';
import { usernameCacheService, useUsernameCacheVersion } from '../../services/usernameCache';
import { PlayerProfile } from '../PlayerProfile';
import { CreatorProfile } from '../CreatorProfile';
import { SectionHeader } from '../ui/SectionHeader';
import { SkeletonLoader } from '../SkeletonLoader';
import { cn } from '../../utils/cn';
import type { IncidentAssignee } from '../../types/incident';

export interface IncidentAssigneesSectionProps {
  incidentId: string;
  canManage: boolean;
  /**
   * When true, the component routes mutations through `*-with-sync` companions so changes
   * propagate to linked kanban cards (per per-link `sync_assignees` flag). When false,
   * the legacy add/remove endpoints are used (zero side effects).
   */
  useSync: boolean;
  /** Bumped by parent when an `incident.assignee_changed` WS event is received. */
  refreshNonce?: number;
  /** Called whenever the local list changes (parent may want to recount). */
  onAssigneesChanged?: (assignees: IncidentAssignee[]) => void;
  /** Optional compact rendering for sidebars. */
  compact?: boolean;
}

/**
 * "Staff Assignees" section for the incident detail page.
 *
 * IMPORTANT: This is **distinct** from "Linked Users" / "Involved Players" which
 * tracks Minecraft players who are SUSPECTS / VICTIMS / REPORTERS. Assignees are
 * the staff members RESPONSIBLE for working the incident.
 */
export const IncidentAssigneesSection = ({
  incidentId,
  canManage,
  useSync,
  refreshNonce = 0,
  onAssigneesChanged,
  compact = false,
}: IncidentAssigneesSectionProps) => {
  const { identity } = useAuth();
  const myUuid = identity?.raw?.custom?.minecraft_uuid;

  const [assignees, setAssignees] = useState<IncidentAssignee[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [removingUuid, setRemovingUuid] = useState<string | null>(null);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const { matches, searching, error: searchError } = useStaffListSearch({
    debouncedQuery,
    enabled: pickerOpen,
  });
  const usernameCacheVersion = useUsernameCacheVersion();

  const assigneeUuidSet = useMemo(
    () => new Set((assignees ?? []).map((a) => a.user_uuid)),
    [assignees],
  );

  const candidateRows = useMemo(
    () =>
      matches
        .filter((s) => !assigneeUuidSet.has(s.minecraft_uuid))
        .map((s) => ({
          uuid: s.minecraft_uuid,
          name: usernameCacheService.getName(s.minecraft_uuid) ?? s.minecraft_uuid,
        })),
    [matches, assigneeUuidSet, usernameCacheVersion],
  );

  const fetchList = async () => {
    if (!incidentId) return;
    setLoading(true);
    setError(null);
    try {
      const list = await incidentsApiService.listAssignees(incidentId);
      setAssignees(list);
      onAssigneesChanged?.(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load assignees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incidentId]);

  // React to WS-driven refresh signals.
  useEffect(() => {
    if (refreshNonce === 0) return;
    void fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNonce]);

  const handleAdd = async (userUuid: string) => {
    setAdding(true);
    setError(null);
    try {
      const created = useSync
        ? await incidentsApiService.addAssigneeWithSync(incidentId, userUuid)
        : await incidentsApiService.addAssignee(incidentId, userUuid);
      const next = [...(assignees ?? []), created];
      setAssignees(next);
      onAssigneesChanged?.(next);
      setQuery('');
      setPickerOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add assignee');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (userUuid: string) => {
    setRemovingUuid(userUuid);
    setError(null);
    try {
      if (useSync) {
        await incidentsApiService.removeAssigneeWithSync(incidentId, userUuid);
      } else {
        await incidentsApiService.removeAssignee(incidentId, userUuid);
      }
      const next = (assignees ?? []).filter((a) => a.user_uuid !== userUuid);
      setAssignees(next);
      onAssigneesChanged?.(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove assignee');
    } finally {
      setRemovingUuid(null);
    }
  };

  const count = assignees?.length ?? 0;
  const meAlreadyAssigned = !!myUuid && assigneeUuidSet.has(myUuid);

  return (
    <div className={cn(compact ? 'subcard p-3' : 'glass-card p-4 md:p-5')}>
      <SectionHeader
        as="h2"
        title="Staff Assignees"
        icon={Users}
        count={count}
        className={cn(compact && 'mb-2')}
        actions={canManage ? (
          <>
            {!meAlreadyAssigned && myUuid && (
              <button
                type="button"
                onClick={() => void handleAdd(myUuid)}
                disabled={adding}
                className="glass-button-secondary btn-xs"
                title="Assign yourself to this incident"
              >
                <UserPlus className="w-3.5 h-3.5" />
                Assign me
              </button>
            )}
            <button
              type="button"
              onClick={() => setPickerOpen((v) => !v)}
              className="btn-icon btn-icon-sm"
              title={pickerOpen ? 'Close staff picker' : 'Add a different staff member'}
              aria-label={pickerOpen ? 'Close staff picker' : 'Add a different staff member'}
              aria-expanded={pickerOpen}
            >
              {pickerOpen ? <XIcon className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
            </button>
          </>
        ) : undefined}
      />

      {!compact && (
        <div className="flex items-start gap-2 text-xs text-glass-500 mb-3">
          <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>
            Staff responsible for working this incident. Distinct from &quot;Linked Users&quot;
            (involved players such as suspects, victims, or reporters).
          </span>
        </div>
      )}

      {/* Picker */}
      {pickerOpen && canManage && (
        <div className="mb-3 space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search staff by username…"
              className="glass-input w-full pl-9"
            />
            {searching && (
              <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-glass-400" />
            )}
          </div>
          {query.length > 0 && query.length < 2 && (
            <p className="text-xs text-glass-500 px-1">Type at least 2 characters…</p>
          )}
          {searchError && <p className="text-xs text-red-400 px-1">{searchError}</p>}
          {candidateRows.length > 0 && (
            <div className="rounded-lg border border-glass-200/10 bg-glass-100/5 overflow-hidden max-h-60 overflow-y-auto">
              {candidateRows.map((row) => (
                <button
                  key={row.uuid}
                  type="button"
                  onClick={() => void handleAdd(row.uuid)}
                  disabled={adding}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-glass-200/10 transition-colors"
                >
                  <PlayerProfile uuid={row.uuid} name={row.name} inline showUuid={false} />
                  <span className="text-[11px] font-mono text-glass-500">
                    {row.uuid.slice(0, 8)}…
                  </span>
                </button>
              ))}
            </div>
          )}
          {debouncedQuery.length >= 2 &&
            !searching &&
            !searchError &&
            candidateRows.length === 0 && (
              <p className="text-xs text-glass-500 px-1">No matching staff (or already assigned)</p>
            )}
        </div>
      )}

      {error && (
        <div className="alert alert-danger mb-3" role="alert">
          <AlertCircle />
          <span>{error}</span>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="space-y-2" aria-busy="true">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className={cn('flex items-center gap-3', compact ? 'py-1.5' : 'subcard p-3')}>
              <SkeletonLoader className="w-7 h-7 rounded-md" />
              <SkeletonLoader className="h-4 w-32" />
            </div>
          ))}
        </div>
      ) : count === 0 ? (
        <p className="text-sm text-glass-400">No staff assigned yet.</p>
      ) : (
        <div className={cn(compact ? 'divide-y divide-glass-200/10' : 'space-y-2')}>
          {(assignees ?? []).map((a) => (
            <div
              key={a.user_uuid}
              className={cn(
                'flex items-center justify-between gap-2',
                compact ? 'py-1.5' : 'subcard p-3',
              )}
            >
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <PlayerProfile uuid={a.user_uuid} showUuid={!compact} inline={compact} />
                {!compact && (
                  <span className="text-xs text-glass-500 tabular-nums">
                    Assigned by <CreatorProfile creatorId={a.assigned_by} inline />{' '}
                    {formatDistanceToNow(new Date(a.assigned_at), { addSuffix: true })}
                  </span>
                )}
              </div>
              {canManage && (
                <button
                  type="button"
                  onClick={() => void handleRemove(a.user_uuid)}
                  disabled={removingUuid === a.user_uuid}
                  className="btn-icon btn-icon-sm text-red-400 hover:text-red-300"
                  title="Remove assignee"
                  aria-label="Remove assignee"
                >
                  {removingUuid === a.user_uuid ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
